import { buildMeasurement } from 'core/service/measurements/measurementUtils';
import { logInfo, logWarn } from 'core-framework/util/LogMessage';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { MeasurementName } from 'core/constant/MeasurementName';
import { IMeasurementTags } from '@agoda/messaging-client';
import { sendMeasurement } from 'core/messaging/measurement/measurement';
import { AvailabilityCalendar } from 'gql/autogen/AvailabilityCalendar';
import { ActivityGqlOperation, GqlApiError } from 'store/api/api.types';
import { getDateDiff } from 'core-framework/util/datetime/date';
import { ActivitiesPageTypes } from 'core/constant/analytics/AnalyticsPageType';
import { MeasurementStatus } from 'core/constant/MeasurementStatus';
import { toSafeString } from 'store/api/apiUtils';
import { FieldValue } from 'core/constant/FieldValue';
import { SearchErrorCode } from 'gql/autogen/SearchErrorCode';
import { SearchSubErrorCode } from 'gql/autogen/SearchSubErrorCode';

const sendCalendarGqlMeasurements = (
    message: string,
    measurementName: MeasurementName,
    pageTypeId: ActivitiesPageTypes,
    measurementTags: IMeasurementTags,
    config: BootstrapConfig,
): boolean => {
    const measurement = buildMeasurement(ActivityGqlOperation.calendar, measurementName, pageTypeId, message, 1, false, config);
    measurement.tags = {
        ...measurement.tags,
        ...measurementTags,
    };

    sendMeasurement({ ...measurement, name: MeasurementName.GqlCalendarStatus }, pageTypeId);

    if (measurementName === MeasurementName.GqlCalendarSuccess) {
        logInfo(message, pageTypeId, measurement.tags);
        return true;
    }
    logWarn(message, pageTypeId, measurement.tags);
    return true;
};

const isMismatchSchedules = (scheduleLength = 0, startDate = '', endDate = ''): boolean => {
    const dateDiff = getDateDiff(endDate, startDate);
    return dateDiff !== scheduleLength;
};

export enum CalendarMeasurementMessage {
    Success = 'success',
    MismatchSchedule = 'mismatch schedules information',
    NoSchedule = 'no schedules information',
}

export const measureCalendarApiResponse = (
    isSuccess: boolean,
    pageTypeId: number,
    config: BootstrapConfig,
    error?: GqlApiError,
    availabilityCalendar?: AvailabilityCalendar,
): boolean => {
    const messageLowerCase = toSafeString(error?.message).toLowerCase();
    const measurementTags: IMeasurementTags = {
        activityId: toSafeString(availabilityCalendar?.masterActivityId),
        supplierId: toSafeString(availabilityCalendar?.masterSupplierId),
        statusCode: toSafeString(error?.status),
        errorCode: error?.errorCode || SearchErrorCode.EMPTY_ERROR_CODE,
        subErrorCode: error?.subErrorCode || SearchSubErrorCode.EMPTY_SUB_ERROR_CODE,
    };
    if (isSuccess) {
        measurementTags.status = MeasurementStatus.Success;
        const schedules = availabilityCalendar?.schedules || [];
        if (schedules.length === 0) {
            return sendCalendarGqlMeasurements(CalendarMeasurementMessage.NoSchedule, MeasurementName.GqlCalendarNoSchedule, pageTypeId, measurementTags, config);
        }
        const isMismatch = isMismatchSchedules(schedules.length, availabilityCalendar?.startDate, availabilityCalendar?.endDate);
        if (isMismatch) {
            measurementTags.schedulesLength = String(schedules.length);
            measurementTags.startDate = availabilityCalendar?.startDate || FieldValue.Undefined;
            measurementTags.endDate = availabilityCalendar?.endDate || FieldValue.Undefined;
            return sendCalendarGqlMeasurements(CalendarMeasurementMessage.MismatchSchedule, MeasurementName.GqlCalendarScheduleMismatch, pageTypeId, measurementTags, config);
        }
        return sendCalendarGqlMeasurements(CalendarMeasurementMessage.Success, MeasurementName.GqlCalendarSuccess, pageTypeId, measurementTags, config);
    }

    measurementTags.status = MeasurementStatus.Failed;
    return sendCalendarGqlMeasurements(messageLowerCase, MeasurementName.GqlCalendarError, pageTypeId, measurementTags, config);
};
